// src/context/AuthContext.js
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { auth } from "../firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendEmailVerification,
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence
} from "firebase/auth";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const validateEmail = useCallback((email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).trim().toLowerCase());
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (user) => {
        if (user) {
          try {
            await user.getIdToken(true);
            setCurrentUser(user);
            setAuthError(null);
          } catch (error) {
            console.error("Token verification failed:", error);
            await signOut(auth);
            setCurrentUser(null);
          }
        } else {
          setCurrentUser(null);
        }
        setIsLoading(false);
      },
      (error) => {
        console.error("Auth state error:", error);
        setAuthError(error.message);
        setIsLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  const register = useCallback(
    async (name, email, password) => {
      try {
        setIsLoading(true);
        setAuthError(null);

        const cleanedEmail = email.trim();
        if (!validateEmail(cleanedEmail)) {
          throw new Error("Inserisci un indirizzo email valido");
        }
        if (password.length < 6) {
          throw new Error("La password deve contenere almeno 6 caratteri");
        }

        await setPersistence(auth, browserLocalPersistence);
        const userCredential = await createUserWithEmailAndPassword(auth, cleanedEmail, password);

        if (name && name.trim() !== "") {
          await updateProfile(userCredential.user, { displayName: name.trim() });
        }

        await sendEmailVerification(userCredential.user);
        return { success: true, user: userCredential.user };
      } catch (error) {
        let errorMessage = "Registrazione fallita";
        switch (error.code) {
          case "auth/email-already-in-use":
            errorMessage = "Email già registrata";
            break;
          case "auth/invalid-email":
            errorMessage = "Email non valida";
            break;
          case "auth/weak-password":
            errorMessage = "Password troppo debole";
            break;
          default:
            errorMessage = error.message || errorMessage;
        }
        setAuthError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsLoading(false);
      }
    },
    [validateEmail]
  );

  // ✅ Opzione A: rilancia l'errore per farlo gestire al componente (es. Login.jsx)
  const login = useCallback(
    async (email, password) => {
      setIsLoading(true);
      setAuthError(null);
      try {
        const cleanedEmail = email.trim();

        // Mantieni un controllo minimale ma senza "enumeration"
        if (!validateEmail(cleanedEmail)) {
          const e = new Error("Invalid email or password");
          e.code = "auth/invalid-credential";
          throw e;
        }

        await setPersistence(auth, browserSessionPersistence);
        const cred = await signInWithEmailAndPassword(auth, cleanedEmail, password);

        // Facoltativo: refresh token
        await cred.user.getIdToken(true);

        return cred; // { user: ... }
      } catch (error) {
        // Non fare signOut qui e non convertire in {success:false}: lascia che il chiamante gestisca err.code
        setAuthError(null);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [validateEmail]
  );

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      setAuthError(null);
      await signOut(auth);
      return { success: true };
    } catch (error) {
      setAuthError(error.message);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value = {
    user: currentUser,
    register,
    login,
    logout,
    isLoading,
    error: authError,
    isAuthenticated: !!currentUser,
    isEmailVerified: currentUser?.emailVerified || false
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve essere usato dentro un AuthProvider");
  }
  return context;
};
